import React from 'react';
import {useTranslation} from 'react-i18next';
import {PersonAssignment, WorkshopAssignment} from '../../../admin/artisticobjects/types/ArtisticObject';

interface DetailsArtisansListProps {
  personAssignments?: PersonAssignment[];
  workshopAssignments?: WorkshopAssignment[];
  creatorsText?: string;
  designersText?: string;
}

interface ArtisanItem {
  name: string;
  uncertain: boolean;
}

const DetailsArtisansList: React.FC<DetailsArtisansListProps> = ({
  personAssignments = [],
                                                                   workshopAssignments = [],
                                                                   creatorsText,
                                                                   designersText
}) => {
  const {t} = useTranslation();
  const formatPersonItem = (person: PersonAssignment): ArtisanItem => ({
    name: `${person.name} ${person.surname}`,
    uncertain: person.uncertain
  });

  const formatWorkshopItem = (workshop: WorkshopAssignment): ArtisanItem => {
    const name = workshop.parent
      ? `${workshop.name} (${workshop.parent.name})`
      : workshop.name;
    return {
      name,
      uncertain: workshop.uncertain
    };
  };

  const getArtisansByType = (type: string): ArtisanItem[] => {
    const people = personAssignments
      .filter(p => p.type === type)
      .map(formatPersonItem);

    const workshops = workshopAssignments
      .filter(w => w.type === type)
      .map(formatWorkshopItem);

    return [...people, ...workshops];
  };

  const creatorsFromAssignments = getArtisansByType('creator');
  const designersFromAssignments = getArtisansByType('designer');

  // Use text field if available, otherwise use generated list
  const hasCreators = creatorsText || creatorsFromAssignments.length > 0;
  const hasDesigners = designersText || designersFromAssignments.length > 0;

  if (!hasCreators && !hasDesigners) {
    return null;
  }

  const renderArtisanList = (artisans: ArtisanItem[]) => {
    return (
      <div className="space-y-1">
        {artisans.map((artisan, index) => (
          <div key={index} className="text-sm text-stone-700 flex items-center gap-2">
            <span>{artisan.name}</span>
            {artisan.uncertain && (
              <i className="fa-regular fa-question-circle text-gray-600 cursor-pointer"
                 title={t('details.uncertain')}></i>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderTextWithUncertainty = (text: string) => {
    const parts = text.split('(?)');

    return (
      <div className="text-sm text-stone-700">
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {part}
            {index < parts.length - 1 && (
              <i className="fa-regular fa-question-circle text-gray-600 mx-1 cursor-pointer"
                 title={t('details.uncertain')}></i>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div id="artisans-card" className="bg-white shadow-sm p-6">
      {hasCreators && (
        <div className={hasDesigners ? 'mb-4' : ''}>
          <h3 className="text-lg font-semibold text-stone-900 mb-3">{t('details.sections.creators')}</h3>
          {creatorsText ? (
            renderTextWithUncertainty(creatorsText)
          ) : (
            renderArtisanList(creatorsFromAssignments)
          )}
        </div>
      )}

      {hasDesigners && (
        <div>
          <h3 className="text-lg font-semibold text-stone-900 mb-3">{t('details.sections.designers')}</h3>
          {designersText ? (
            renderTextWithUncertainty(designersText)
          ) : (
            renderArtisanList(designersFromAssignments)
          )}
        </div>
      )}
    </div>
  );
};

export default DetailsArtisansList;

